import React, { useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import type { Questionnaire, QuestionnaireOverrides, Answer, EnhancedQuestion, Error, RenderTextArgs, RenderQuestionTopContentArgs, RenderQuestionBottomContentArgs, QuestionnaireHandle, ProcessFileOnDrop } from '../types';
import { createAnswersMap, createQuestionsMap, createQuestionsMapWithErrors } from '../utils';
import { useScrollQuestions } from '../hooks/use-scroll-questions';
export type QuestionnaireContextValue = {
  questionnaireAnswers: Map<string, Answer>;
  questionsMap: Map<string, EnhancedQuestion>;
  overrides: QuestionnaireOverrides;
  setAnswerForQuestion: (questionId: string, answers: string[]) => void;
  renderText: (args: RenderTextArgs) => React.ReactNode | null;
  renderQuestionTopContent: (args: RenderQuestionTopContentArgs) => React.ReactNode | null;
  renderQuestionBottomContent: (args: RenderQuestionBottomContentArgs) => React.ReactNode | null;
  registerQuestionNode: (questionId: string, node: HTMLElement | null) => void;
  processFileOnDrop?: ProcessFileOnDrop;
};
const QuestionnaireContext = React.createContext<QuestionnaireContextValue | undefined>(undefined);
type QuestionnaireProviderProps = {
  questionnaire: Questionnaire;
  questionnaireAnswers: Answer[];
  errors: Error[];
  overrides: QuestionnaireOverrides;
  children?: React.ReactNode;
  onQuestionAnswered: (answerMap: Map<string, Answer>, questionId: string, answers: string[]) => void;
  onAnswerStateChanged: (answerMap: Map<string, Answer>) => void;
  renderText: (args: RenderTextArgs) => React.ReactNode | null;
  renderQuestionTopContent: (args: RenderQuestionTopContentArgs) => React.ReactNode | null;
  renderQuestionBottomContent: (args: RenderQuestionBottomContentArgs) => React.ReactNode | null;
  questionnaireRef: React.Ref<QuestionnaireHandle>;
  processFileOnDrop?: ProcessFileOnDrop;
};
function QuestionnaireProvider({
  questionnaire,
  questionnaireAnswers: initialAnswers,
  overrides,
  errors,
  children,
  onQuestionAnswered,
  onAnswerStateChanged,
  renderText,
  renderQuestionTopContent,
  renderQuestionBottomContent,
  questionnaireRef,
  processFileOnDrop
}: QuestionnaireProviderProps): React.ReactElement {
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<Map<string, Answer>>(createAnswersMap(initialAnswers));
  // Storing the onAnswerStateChanged callback in a ref to avoid re-triggering the effect when it changes.
  const onAnswerStateChangedRef = useRef<QuestionnaireProviderProps['onAnswerStateChanged']>(onAnswerStateChanged);
  // eslint-disable-next-line react-hooks/refs
  onAnswerStateChangedRef.current = onAnswerStateChanged;
  const {
    registerQuestionNode
  } = useScrollQuestions(questionnaireRef);
  const questionsMap = useMemo(() => createQuestionsMap(questionnaire), [questionnaire]);
  const questionsMapWithErrors = useMemo(() => {
    return createQuestionsMapWithErrors(questionsMap, errors);
  }, [questionsMap, errors]);
  useEffect(() => {
    if (onAnswerStateChangedRef.current) {
      onAnswerStateChangedRef.current(questionnaireAnswers);
    }
  }, [questionnaireAnswers]);
  const setAnswerForQuestion = useCallback((questionId: string, answers: string[]) => {
    setQuestionnaireAnswers(questionnaireAnswers => {
      onQuestionAnswered(questionnaireAnswers, questionId, answers);
      const newQuestionnaireAnswers = structuredClone(questionnaireAnswers);
      const existingAnswer = questionnaireAnswers.get(questionId) ?? {
        answers: [],
        question: questionsMap.get(questionId)?.question ?? '',
        questionId
      };
      const newAnswer = {
        ...existingAnswer,
        answers
      };
      newQuestionnaireAnswers.set(questionId, newAnswer);
      return newQuestionnaireAnswers;
    });
  }, [questionsMap, onQuestionAnswered]);
  const value = useMemo(() => ({
    questionnaireAnswers,
    setAnswerForQuestion,
    questionsMap: questionsMapWithErrors,
    overrides,
    renderText,
    renderQuestionTopContent,
    renderQuestionBottomContent,
    registerQuestionNode,
    processFileOnDrop
  }), [questionnaireAnswers, questionsMapWithErrors, overrides, setAnswerForQuestion, renderText, renderQuestionTopContent, renderQuestionBottomContent, registerQuestionNode, processFileOnDrop]);
  return <QuestionnaireContext.Provider value={value}>{children}</QuestionnaireContext.Provider>;
}
function useQuestionnaireContext(): QuestionnaireContextValue {
  const ctx = useContext(QuestionnaireContext);
  if (!ctx) {
    throw new Error('useQuestionnaireContext must be used within a QuestionnaireProvider');
  }
  return ctx;
}
export { QuestionnaireProvider, useQuestionnaireContext };